/**
 * 跨设备续局：查本账号还有没有坐着的牌桌。
 *
 * 「刷新不丢房间」现在靠地址栏的 `/r/CODE`，但那只覆盖**同一设备刷新**。
 * 换一台手机、清了缓存、或者从收藏夹/首页直接打开站点时，地址栏里什么都没有——
 * 而用户最早提的核心需求正是「单账号换设备时候的游戏继续」。
 *
 * 服务端的 `GET /api/me/active-seats` 就是为此准备的（它的注释写着「没有这个端点，
 * 用户得记住房间码才能回去」），但客户端一直没接上这根线。这个模块就是那根线。
 */
import { useEffect, useState } from 'react'
import type { Chips, RoomCode, RoomId, SeatIndex } from '@poker/shared'

import { apiBaseUrl } from './netUrl.js'
import { onlineSession } from './session.js'

/**
 * 与服务端 `ActiveSeatView` 逐字段对应（packages/server/src/db/repos/rooms.ts）。
 * `seat` 为 null：没坐着，只在那个房间里有托管筹码（起立 / 离开房间之后）—— 回去坐下就能领回来，
 * 所以同样列在「你还有一桌没打完」里；`stack` 此时是托管额。
 */
export interface ActiveSeat {
  readonly roomId: RoomId
  readonly roomCode: RoomCode
  readonly roomPhase: string
  readonly seat: SeatIndex | null
  readonly stack: Chips
  readonly presence: string | null
  readonly graceEndsAt: number | null
  readonly handNo: number
  readonly playerCount: number
  readonly lastActivityAt: number
}

/**
 * 拉取可续局的座位。任何失败都返回空数组而不是抛错：
 * 这是个锦上添花的入口，它挂了不该把整个大厅拖垮。
 */
export async function fetchActiveSeats(fetchImpl: typeof fetch = globalThis.fetch): Promise<ActiveSeat[]> {
  const s = onlineSession()
  if (s === null) return []
  try {
    const token = await s.auth.ensureAccessToken()
    const res = await fetchImpl(`${apiBaseUrl()}/api/me/active-seats`, {
      headers: { authorization: `Bearer ${token}` },
    })
    if (!res.ok) return []
    const body = (await res.json()) as { seats?: readonly ActiveSeat[] }
    return [...(body.seats ?? [])]
  } catch {
    return []
  }
}

/**
 * 「重新查一遍」的广播。
 *
 * ★ 换身份（邮箱登录 / 兑换邮件链接）之后**必须**叫一次。这个 hook 只在 `enabled` 变化时才发请求，
 *   而兑换前后 `enabled` 一直是 true —— 结果是兑换面板说「已找回，你的牌桌都在」，
 *   大厅里那张「你还有一桌没打完」却是上一个账号的答案（空），用户找不到回桌的路，
 *   除非他自己想到刷新页面。
 */
const seatsListeners = new Set<() => void>()
let seatsVersion = 0

/** 让所有挂载中的 `useActiveSeats` 立刻按**当前身份**重查一次。 */
export function refreshActiveSeats(): void {
  seatsVersion += 1
  for (const notify of [...seatsListeners]) notify()
}

/**
 * @param enabled 只在「已连上服务器 且 人不在牌桌上」时才查。
 *                在牌桌上还去查等于自己问自己，纯属浪费一次请求。
 */
export function useActiveSeats(enabled: boolean): readonly ActiveSeat[] {
  const [seats, setSeats] = useState<readonly ActiveSeat[]>([])
  const [version, setVersion] = useState(seatsVersion)

  useEffect(() => {
    const notify = (): void => setVersion(seatsVersion)
    seatsListeners.add(notify)
    return () => {
      seatsListeners.delete(notify)
    }
  }, [])

  useEffect(() => {
    if (!enabled) {
      setSeats([])
      return
    }
    let alive = true
    void fetchActiveSeats().then((s) => {
      if (alive) setSeats(s)
    })
    return () => {
      alive = false
    }
  }, [enabled, version])
  return seats
}
